import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonUtilsService } from '../shared/common-utils.service';
import { Config } from '../models/ui-config';
import { FormGroup } from '@angular/forms';
import { HttpMethods } from '../enums/http-method.enum';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FileStats, FileStatsEvent } from '../models/file-stats';
import { JsonDynamicComponentService } from '../json-dynamic-component.service';

@Component({
  selector: 'ra-csv-utils',
  templateUrl: './csv-utils.component.html',
  styleUrls: ['./csv-utils.component.scss'],
  standalone: false,
})
export class CsvUtilsComponent implements OnInit, OnDestroy {
  delimiter: string;
  fileName: string = '';
  inputId = CommonUtilsService.createGUID();
  isDownloading: boolean = false;
  fileStatsMessage: string = '';
  id = CommonUtilsService.createGUID();
  private valueChangeSubscription: Subscription;
  private lastFetchedUrl: string = '';

  @Input() readOnly: boolean;
  @Input() currentConfig: Config;
  @Input() parentFormGroup: FormGroup;
  @Input() parentConfig: FormGroup;
  @Output() fileSelect = new EventEmitter<FileStatsEvent>();

  constructor(
    private commonUtils: CommonUtilsService,
    private toastr: ToastrService,
    private jsonDynamicComponentService: JsonDynamicComponentService,
  ) {}

  ngOnInit(): void {
    this.jsonDynamicComponentService.addCsvUtilsControl(this.id, this);
    this.fileName = this.currentConfig?.property.split('_UTILS')[0];
    this.delimiter = this.currentConfig?.delimiter;

    this.valueChangeSubscription = this.parentConfig.valueChanges
      .pipe(debounceTime(250))
      .subscribe(() => {
        this.fetchFileStats();
      });

    // Initial fetch of file stats if available
    if (this.currentConfig?.fileStatsURL) {
      this.fetchFileStats();
    }
  }

  updateCsvFileStatsMsg(): void {
    this.lastFetchedUrl = '';
    this.fetchFileStats();
  }

  onFileSelect(event: Event) {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];

    if (file) {
      // Format file size with appropriate unit (B, KB, MB, GB)
      const fileSize = this.formatFileSize(file.size);

      // Set initial message with available information
      this.fileStatsMessage = `Selected file "${file.name}" with size ${fileSize}. Uploading and analyzing file...`;

      // If the fileName wasn't set, update it with the selected file name
      if (!this.fileName) {
        this.fileName = file.name;
      }

      // Emit the file selection event to the parent component
      this.fileSelect.emit({
        selectedFile: event,
        delimiter: this.delimiter,
        fileName: this.fileName,
      });
    }
  }

  /**
   * Formats file size in bytes to a human-readable string with appropriate unit
   * @param bytes File size in bytes
   * @returns Formatted string with appropriate unit (B, KB, MB, GB)
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));

    // Ensure we don't exceed our units array
    const unitIndex = Math.min(i, units.length - 1);

    // Calculate the value in the appropriate unit with 2 decimal places
    return (
      (bytes / Math.pow(1024, unitIndex)).toFixed(2) + ' ' + units[unitIndex]
    );
  }

  /**
   * Fetches file statistics from the server with retry logic
   * @param retryCount Number of retries attempted
   * @param maxRetries Maximum number of retries
   */
  public fetchFileStats(retryCount: number = 0, maxRetries: number = 5): void {
    const fileStatsUrl = this.getInterpolatedValue(
      this.currentConfig?.fileStatsURL,
    );

    if (!fileStatsUrl) {
      return;
    }

    // Don't retry with the same URL unless explicitly called from a retry
    if (fileStatsUrl === this.lastFetchedUrl && retryCount === 0) {
      return;
    }

    this.lastFetchedUrl = fileStatsUrl;

    this.commonUtils.getHttpCall(HttpMethods.Get, fileStatsUrl).subscribe(
      (res: FileStats) => {
        if (
          res &&
          (res.no_of_rows !== undefined || res.no_of_columns !== undefined)
        ) {
          this.fileSelect.emit({
            selectedFile: null,
            delimiter: this.delimiter,
            fileName: this.fileName,
            rowCount: res.no_of_rows,
          });

          // Format the file size if it's not already formatted
          let fileSize = res.file_size;
          if (typeof res.file_size === 'number') {
            fileSize = this.formatFileSize(res.file_size as number);
          }

          this.fileStatsMessage = `The file contains ${res.no_of_rows.toString()} rows, ${res.no_of_columns.toString()} columns and is ${fileSize} in size.`;
        } else if (retryCount < maxRetries) {
          // If response is empty or incomplete but we haven't reached max retries, try again
          setTimeout(() => {
            this.fetchFileStats(retryCount + 1, maxRetries);
          }, 2000); // Wait 2 seconds before retrying
        } else {
          this.fileStatsMessage = 'Unable to fetch complete file statistics';
        }
      },
      (_err) => {
        if (retryCount < maxRetries) {
          setTimeout(() => {
            this.fetchFileStats(retryCount + 1, maxRetries);
          }, 2000); // Wait 2 seconds before retrying
        } else {
          this.fileStatsMessage =
            'Error fetching file stats after multiple attempts';
        }
      },
    );
  }

  private getInterpolatedValue(expr: string, value: any = {}) {
    if (expr) {
      expr = this.commonUtils.getInterpolatedValue(expr, {
        ...value,
        ...this.parentConfig.value,
      });
    }
    return expr;
  }

  onDownloadClick() {
    this.toastr.info('Downloading file.');
    this.isDownloading = true;
    const downloadUrl = this.getInterpolatedValue(
      this.currentConfig?.downloadURL,
    );
    this.commonUtils.getHttpCall(HttpMethods.Get, downloadUrl).subscribe(
      (result) => {
        this.isDownloading = false;
        window.open(result['presigned_url'], '_parent');
        this.toastr.success('File downloaded successfully.');
      },
      (_error) => {
        this.isDownloading = false;
        this.toastr.error('Error while downloading plot.');
      },
    );
  }

  onViewDataClick() {}

  ngOnDestroy(): void {
    if (this.valueChangeSubscription) {
      this.valueChangeSubscription.unsubscribe();
    }
  }
}
